// The months the Dashboard's Claims Trend chart draws (useDashboardFilteredData.ts,
// filteredMonthlyBreakdown).
//
// With the date filter on All time the chart is headed "Claims Trend (Last 12
// Months)", so it shows exactly that: the 12 calendar months ending with the
// current month, oldest first, with a month that has no claim drawn at zero so
// the line stays continuous. Before this, it drew every month that ever had a
// claim (27 months in production) under the same heading.
//
// With a date filter set, it shows the months in the range that have a claim,
// as it always has; the rows reaching here are already narrowed to the range.
//
// Labels are "Mon YYYY" in both cases, so a window that crosses a year (Dec
// 2025 to Nov 2026) never shows two months with the same label.
//
// Pure TypeScript, no React import, so claimsTrendWindow.test.ts can pin it
// with Deno.

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const TREND_WINDOW_MONTHS = 12;

export interface TrendClaimRow {
  claimMonth: number;
  claimYear: number;
  amount: number;
}

export interface TrendMonth {
  month: number;
  monthName: string;
  amount: number;
}

export interface CalendarMonth {
  month: number;
  year: number;
}

function label(month: number, year: number): string {
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

function keyOf(month: number, year: number): number {
  return year * 12 + (month - 1);
}

/** The 12 calendar months ending with `current`, oldest first. */
export function lastTwelveMonths(current: CalendarMonth): CalendarMonth[] {
  const end = keyOf(current.month, current.year);
  return Array.from({ length: TREND_WINDOW_MONTHS }, (_, i) => {
    const k = end - (TREND_WINDOW_MONTHS - 1) + i;
    return { month: (k % 12) + 1, year: Math.floor(k / 12) };
  });
}

/**
 * The Claims Trend points. `rows` are the claim lines that already passed the
 * project, builder, status and (when set) date filters.
 */
export function claimsTrendBreakdown(
  rows: TrendClaimRow[],
  options: { hasDateFilter: boolean; current: CalendarMonth },
): TrendMonth[] {
  const totals = new Map<number, number>();
  for (const row of rows) {
    if (!(row.claimMonth >= 1 && row.claimMonth <= 12)) continue;
    const k = keyOf(row.claimMonth, row.claimYear);
    totals.set(k, (totals.get(k) ?? 0) + row.amount);
  }

  if (!options.hasDateFilter) {
    return lastTwelveMonths(options.current).map(({ month, year }) => ({
      month,
      monthName: label(month, year),
      amount: totals.get(keyOf(month, year)) ?? 0,
    }));
  }

  return [...totals.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([k, amount]) => {
      const month = (k % 12) + 1;
      return { month, monthName: label(month, Math.floor(k / 12)), amount };
    });
}
